import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  X,
  ArrowRight,
  ArrowLeft,
  Lock,
  Mail,
  Gamepad2,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  Lightbulb,
  Gift,
  CheckCircle2,
  Clock,
  RotateCw,
  Store,
  Check,
  ChevronDown,
  ShieldCheck,
  AlertTriangle,
} from 'lucide-react';
import { useUIStore } from '../../stores/useUIStore';
import { useAuthStore, AuthUser } from '../../stores/useAuthStore';
import { useCurrencyStore } from '../../stores/useCurrencyStore';
import { SupportedCurrency } from '../../types';
import { lockScroll, unlockScroll } from '../../utils/scrollLock';

// Common typo corrections helper
function suggestEmailCorrection(email: string): string | null {
  if (!email || !email.includes('@')) return null;
  const parts = email.split('@');
  if (parts.length !== 2) return null;
  const domain = parts[1].toLowerCase();
  const typos: Record<string, string> = {
    'gamil.com': 'gmail.com',
    'gmai.com': 'gmail.com',
    'gmaill.com': 'gmail.com',
    'gmial.com': 'gmail.com',
    'hotmial.com': 'hotmail.com',
    'hotmai.com': 'hotmail.com',
    'hotmali.com': 'hotmail.com',
    'homtail.com': 'hotmail.com',
    'outlok.com': 'outlook.com',
    'outloo.com': 'outlook.com',
    'outlock.com': 'outlook.com',
    'yahho.com': 'yahoo.com',
    'yaho.com': 'yahoo.com',
  };
  if (typos[domain]) {
    return `${parts[0]}@${typos[domain]}`;
  }
  return null;
}

function cleanInputPhoneNumber(val: string, prefix: '+54' | '+52'): string {
  if (!val) return '';
  let cleaned = val.replace(/[^\d\s-]/g, '').trim();
  if (prefix === '+52' && /^52\s*/.test(cleaned)) {
    cleaned = cleaned.replace(/^52\s*/, '');
  } else if (prefix === '+54' && /^54\s*/.test(cleaned)) {
    cleaned = cleaned.replace(/^54\s*/, '');
  }
  return cleaned;
}

const GOOGLE_CLIENT_ID =
  '409741371218-i5ih87rnm7bjt1t4c2gkceqs8bfm4cf0.apps.googleusercontent.com';

export type RegStep = 1 | 2 | 3 | 4 | 5 | 6;

export const AuthModal: React.FC = () => {
  const isOpen = useUIStore((s) => s.isAuthModalOpen);
  const initialTab = useUIStore((s) => s.authModalTab);
  const close = useUIStore((s) => s.closeAuthModal);
  const navigateTo = useUIStore((s) => s.navigateTo);

  const loginToStore = useAuthStore((s) => s.login);
  const setUser = useAuthStore((s) => s.setUser);
  const authUser = useAuthStore((s) => s.user);

  const storeCurrency = useCurrencyStore((s) => s.currency);
  const setStoreCurrency = useCurrencyStore((s) => s.setCurrency);

  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [regStep, setRegStep] = useState<RegStep>(1);

  // Form states - Login
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPass, setShowLoginPass] = useState(false);

  // Form states - Register Step 1
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [showRegPass1, setShowRegPass1] = useState(false);
  const [showRegPass2, setShowRegPass2] = useState(false);

  // Step 2 & 3: Epic Games Nick & Bots
  const [epicUsername, setEpicUsername] = useState('');
  const [epicUserInfo, setEpicUserInfo] = useState<any>(null);
  const [botCheckResult, setBotCheckResult] = useState<any>(null);
  const [privacyBlockedInfo, setPrivacyBlockedInfo] = useState<any>(null);
  const [botRequestsSent, setBotRequestsSent] = useState(false);

  // Step 4: Currency & Phone
  const [selectedCurrency, setSelectedCurrency] = useState<SupportedCurrency>(
    () => storeCurrency || 'ARS'
  );
  const [phonePrefix, setPhonePrefix] = useState<'+54' | '+52'>('+54');
  const [phoneNumber, setPhoneNumber] = useState('');

  // Step 5: Email Verification
  const [verificationCode, setVerificationCode] = useState('');
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [verifySuccess, setVerifySuccess] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Step 6: Success
  const [promoRewardAwarded, setPromoRewardAwarded] = useState<number>(0);
  const [createdUser, setCreatedUser] = useState<AuthUser | null>(null);

  // UI status
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Promo referral code
  const [pendingPromo] = useState<string | null>(() => {
    try {
      const urlPromo = (
        new URLSearchParams(window.location.search).get('ref') ||
        new URLSearchParams(window.location.search).get('promo') ||
        ''
      ).trim().toUpperCase();
      if (urlPromo && !localStorage.getItem('fn_pending_promo')) {
        localStorage.setItem('fn_pending_promo', urlPromo);
      }
      return localStorage.getItem('fn_pending_promo') || urlPromo || null;
    } catch {
      return null;
    }
  });

  const [isClosing, setIsClosing] = useState(false);
  const [isGoogleUser, setIsGoogleUser] = useState(false);
  const loginGoogleRef = useRef<HTMLDivElement | null>(null);
  const registerGoogleRef = useRef<HTMLDivElement | null>(null);

  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    unlockScroll('auth-modal');
    setTimeout(() => {
      setIsClosing(false);
      close();
    }, 160);
  }, [isClosing, close]);

  // Sync initial state when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      setRegStep(1);
      setErrorMessage(null);
      setIsClosing(false);
      setIsGoogleUser(false);
      setEpicUserInfo(null);
      setBotCheckResult(null);
      setPrivacyBlockedInfo(null);
      setBotRequestsSent(false);
      setVerificationCode('');
      setVerifyError(null);
      setVerifySuccess(null);
      setResendCooldown(0);
      setPromoRewardAwarded(0);

      const initCurr = storeCurrency === 'MXN' ? 'MXN' : 'ARS';
      setSelectedCurrency(initCurr);
      setPhonePrefix(initCurr === 'MXN' ? '+52' : '+54');
      setPhoneNumber('');

      lockScroll('auth-modal');
    } else {
      unlockScroll('auth-modal');
    }
    return () => {
      unlockScroll('auth-modal');
    };
  }, [isOpen, initialTab, storeCurrency]);

  // Cooldown timer for email verification
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Escape key listener - protected during multi-step registration (steps 2 to 5)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        if (activeTab === 'register' && regStep >= 2 && regStep <= 5) {
          return;
        }
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, activeTab, regStep, handleClose]);

  // Handle Backdrop click (prevent closing during multi-step registration)
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      if (activeTab === 'register' && regStep >= 2 && regStep <= 5) {
        return;
      }
      handleClose();
    }
  };

  // =========================================================================
  // LOGIN SUBMIT
  // =========================================================================
  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let email = loginEmail.trim();
    const suggested = suggestEmailCorrection(email);
    if (suggested) {
      email = suggested;
      setLoginEmail(suggested);
    }

    if (!email || !loginPassword) {
      setErrorMessage('Por favor completa todos los campos.');
      return;
    }

    setIsLoading(true);
    setLoadingMsg('Iniciando sesión...');
    setErrorMessage(null);

    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password: loginPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Credenciales incorrectas.');
      }

      loginToStore(data.user, data.access_token);
      if (data.promo_reward_vbucks) {
        setPromoRewardAwarded(data.promo_reward_vbucks);
      }
      try {
        localStorage.removeItem('fn_pending_promo');
      } catch {}

      if (data.needs_onboarding || !data.user.fortnite_username) {
        setIsGoogleUser(false);
        setActiveTab('register');
        setRegStep(2);
        setEpicUsername(data.user.fortnite_username || '');
        setIsLoading(false);
      } else {
        setIsLoading(false);
        handleClose();
      }
    } catch (err: any) {
      setIsLoading(false);
      setErrorMessage(err.message || 'Error al iniciar sesión.');
    }
  };

  // =========================================================================
  // REGISTER STEP 1 SUBMIT (Email, Password, Confirm Password)
  // =========================================================================
  const handleRegisterStep1 = async (e: React.FormEvent) => {
    e.preventDefault();
    let email = regEmail.trim();
    const suggested = suggestEmailCorrection(email);
    if (suggested) {
      email = suggested;
      setRegEmail(suggested);
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setErrorMessage('Por favor ingresa un correo electrónico válido.');
      return;
    }

    if (regPassword.length < 8) {
      setErrorMessage('La contraseña debe tener un mínimo de 8 caracteres.');
      return;
    }

    if (regPassword !== regConfirmPassword) {
      setErrorMessage('Las dos contraseñas no coinciden. Por favor verifícalas.');
      return;
    }

    setIsLoading(true);
    setLoadingMsg('Verificando correo...');
    setErrorMessage(null);

    try {
      const res = await fetch('/api/auth/check-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(
          data.detail || 'Este correo electrónico ya está registrado.'
        );
      }

      setIsLoading(false);
      setRegStep(2);
    } catch (err: any) {
      setIsLoading(false);
      setErrorMessage(err.message || 'Error al validar el correo.');
    }
  };

  // =========================================================================
  // REGISTER STEP 2 SUBMIT (Epic Games Nickname Search & Lookup)
  // =========================================================================
  const handleRegisterStep2 = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanNick = epicUsername.trim();
    if (!cleanNick || cleanNick.length < 3) {
      setErrorMessage(
        'Ingresa tu nickname de Fortnite (mínimo 3 caracteres).'
      );
      return;
    }

    setIsLoading(true);
    setLoadingMsg('Buscando jugador en Epic Games y consolas...');
    setErrorMessage(null);

    try {
      const res = await fetch('/api/check/lookup-and-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: cleanNick, total_vbucks: 1000 }),
      });
      const data = await res.json();
      if (!res.ok || !data.user) {
        throw new Error(
          data.detail ||
            'Usuario no encontrado en Epic Games ni en consolas vinculadas.'
        );
      }

      setEpicUserInfo(data.user);
      setBotCheckResult(data);
      setIsLoading(false);
      setRegStep(3);
    } catch (err: any) {
      setIsLoading(false);
      setErrorMessage(
        err.message || 'No se pudo verificar el usuario en Epic Games.'
      );
    }
  };

  // =========================================================================
  // REGISTER STEP 3 SUBMIT (Friendship Bot Requests Trigger)
  // =========================================================================
  const handleRegisterStep3Proceed = async () => {
    const hasBots = Boolean(
      botCheckResult &&
        [
          'FRIENDS_READY',
          'FRIENDS_WAITING_48H',
          'BOT_LIMIT_REACHED',
          'NO_BOT_BALANCE',
          'NO_BOT_ITEM_BALANCE',
        ].includes(botCheckResult.status)
    );

    if (botRequestsSent || (hasBots && !botCheckResult?.can_invite_more)) {
      setRegStep(4);
      return;
    }

    setIsLoading(true);
    setLoadingMsg('Enviando solicitudes desde nuestros bots...');
    setErrorMessage(null);
    setPrivacyBlockedInfo(null);

    try {
      const targetName = epicUserInfo?.display_name || epicUsername.trim();
      const targetAccountId = epicUserInfo?.account_id || null;

      const res = await fetch('/api/check/send-all-bot-friend-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: targetName,
          account_id: targetAccountId,
        }),
      });
      const data = await res.json();

      if (data.privacy_blocked) {
        setIsLoading(false);
        setPrivacyBlockedInfo(data);
        return;
      }

      setBotRequestsSent(true);
      setIsLoading(false);
      setRegStep(4);
    } catch {
      // Even if background bot invite encounters partial error, allow user to proceed
      setBotRequestsSent(true);
      setIsLoading(false);
      setRegStep(4);
    }
  };

  // =========================================================================
  // REGISTER STEP 4 SUBMIT (Currency & Optional Phone Submission)
  // =========================================================================
  const handleRegisterStep4Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanedNumber = cleanInputPhoneNumber(phoneNumber, phonePrefix);
    const digitsOnly = cleanedNumber.replace(/\D/g, '');

    if (
      digitsOnly.length > 0 &&
      (digitsOnly.length < 8 || digitsOnly.length > 13)
    ) {
      setErrorMessage(
        'El número de teléfono ingresado no parece válido. Verifica que no tenga prefijos duplicados.'
      );
      return;
    }

    const fullPhone = digitsOnly ? `${phonePrefix} ${cleanedNumber}` : null;

    // Persist chosen currency globally
    setStoreCurrency(selectedCurrency);
    try {
      localStorage.setItem('fn_currency', selectedCurrency);
    } catch {}

    setIsLoading(true);
    setLoadingMsg(
      isGoogleUser ? 'Guardando tu perfil...' : 'Creando cuenta...'
    );
    setErrorMessage(null);

    const displayName = epicUserInfo?.display_name || epicUsername.trim();
    const accountId = epicUserInfo?.account_id || null;

    try {
      if (isGoogleUser) {
        // Caso A: Google User Onboarding
        const token = useAuthStore.getState().token;
        const res = await fetch('/api/auth/me/onboarding', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            fortnite_username: displayName,
            fortnite_account_id: accountId,
            phone: fullPhone,
          }),
        });
        const updatedUser = await res.json();
        if (!res.ok) {
          throw new Error(
            updatedUser.detail || 'Error al completar el perfil.'
          );
        }

        setUser(updatedUser);
        setCreatedUser(updatedUser);
        setIsLoading(false);
        setRegStep(6);
      } else {
        // Caso B: Standard Email & Password Registration
        const payload: Record<string, any> = {
          email: regEmail.trim(),
          password: regPassword,
          fortnite_username: displayName,
          fortnite_account_id: accountId,
          phone: fullPhone,
        };
        if (pendingPromo) {
          payload.promo_code = pendingPromo;
        }

        const res = await fetch('/api/auth/register', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.detail || 'Error al registrar la cuenta.');
        }

        loginToStore(data.user, data.access_token);
        setCreatedUser(data.user);
        if (data.promo_reward_vbucks) {
          setPromoRewardAwarded(data.promo_reward_vbucks);
        }
        try {
          localStorage.removeItem('fn_pending_promo');
        } catch {}

        setIsLoading(false);
        setRegStep(5);
      }
    } catch (err: any) {
      setIsLoading(false);
      setErrorMessage(err.message || 'Error al procesar el registro.');
    }
  };

  // =========================================================================
  // REGISTER STEP 5: EMAIL VERIFICATION CODE
  // =========================================================================
  const handleVerifyEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = verificationCode.trim();
    if (code.length !== 6) {
      setVerifyError('El código debe tener exactamente 6 dígitos.');
      return;
    }

    setIsLoading(true);
    setLoadingMsg('Verificando código...');
    setVerifyError(null);
    setVerifySuccess(null);

    const targetEmail = createdUser?.email || regEmail.trim();

    try {
      const res = await fetch('/api/auth/verify-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: targetEmail, code }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Código de verificación incorrecto.');
      }

      if (createdUser) {
        setUser({ ...createdUser, is_email_verified: true });
      }
      setVerifySuccess('¡Correo verificado con éxito!');
      setIsLoading(false);
      setTimeout(() => {
        setRegStep(6);
      }, 800);
    } catch (err: any) {
      setIsLoading(false);
      setVerifyError(err.message || 'Error al validar el código.');
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    const targetEmail = createdUser?.email || regEmail.trim();
    setVerifyError(null);
    setVerifySuccess(null);

    try {
      const res = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: targetEmail }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'No se pudo reenviar el código.');
      }

      setVerifySuccess('¡Nuevo código enviado! Revisa tu bandeja o spam.');
      setResendCooldown(60);
    } catch (err: any) {
      setVerifyError(err.message || 'Error al reenviar el código.');
    }
  };

  // =========================================================================
  // GOOGLE SIGN-IN CREDENTIAL CALLBACK
  // =========================================================================
  const handleGoogleCredentialResponse = useCallback(
    async (response: any) => {
      if (!response || !response.credential) return;

      setIsLoading(true);
      setLoadingMsg('Autenticando con Google...');
      setErrorMessage(null);

      try {
        const gPromo = (
          localStorage.getItem('fn_pending_promo') ||
          pendingPromo ||
          ''
        )
          .trim()
          .toUpperCase();
        const payload: Record<string, any> = {
          credential: response.credential,
        };
        if (gPromo) {
          payload.promo_code = gPromo;
        }

        const res = await fetch('/api/auth/google', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.detail || 'Error en autenticación con Google.');
        }

        loginToStore(data.user, data.access_token);
        setCreatedUser(data.user);
        if (data.promo_reward_vbucks) {
          setPromoRewardAwarded(data.promo_reward_vbucks);
        }
        try {
          localStorage.removeItem('fn_pending_promo');
        } catch {}

        if (data.needs_onboarding || !data.user.fortnite_username) {
          setIsGoogleUser(true);
          setActiveTab('register');
          setRegStep(2);
          setEpicUsername(data.user.fortnite_username || '');
          setIsLoading(false);
        } else {
          try {
            fetch('/api/check/send-all-bot-friend-requests', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${data.access_token}`,
              },
              body: JSON.stringify({ username: data.user.fortnite_username }),
            }).catch(() => {});
          } catch {}

          setIsLoading(false);
          handleClose();
        }
      } catch (err: any) {
        setIsLoading(false);
        setErrorMessage(err.message || 'Error al conectar con Google.');
      }
    },
    [pendingPromo, loginToStore, handleClose]
  );

  // Helper para asegurar que el script de Google GSI esté cargado
  const ensureGoogleScript = useCallback((): Promise<boolean> => {
    if (typeof window === 'undefined') return Promise.resolve(false);
    if ((window as any).google?.accounts?.id) return Promise.resolve(true);

    return new Promise((resolve) => {
      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if ((window as any).google?.accounts?.id) {
          clearInterval(interval);
          resolve(true);
        } else if (attempts > 50) {
          clearInterval(interval);
          resolve(false);
        }
      }, 100);

      if (
        !document.querySelector(
          'script[src*="accounts.google.com/gsi/client"]'
        )
      ) {
        const s = document.createElement('script');
        s.src = 'https://accounts.google.com/gsi/client';
        s.async = true;
        s.defer = true;
        document.head.appendChild(s);
      }
    });
  }, []);

  // Inicializar y renderizar botón oficial de Google en la pestaña activa
  useEffect(() => {
    if (!isOpen || regStep !== 1) return;

    let isMounted = true;
    ensureGoogleScript().then((ready) => {
      if (!isMounted || !ready) return;

      const container =
        activeTab === 'login'
          ? loginGoogleRef.current
          : registerGoogleRef.current;
      if (!container || !(window as any).google?.accounts?.id) return;

      try {
        (window as any).google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        container.innerHTML = '';
        (window as any).google.accounts.id.renderButton(container, {
          theme: 'outline',
          size: 'large',
          text: activeTab === 'register' ? 'signup_with' : 'continue_with',
          shape: 'rectangular',
          width: 320,
          logo_alignment: 'left',
        });
      } catch (err) {
        console.warn('[GOOGLE GSI] Error rendering button:', err);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [
    isOpen,
    activeTab,
    regStep,
    ensureGoogleScript,
    handleGoogleCredentialResponse,
  ]);

  // Click manual por si el usuario presiona el botón antes del montaje de Google
  const handleGoogleAuthClick = async () => {
    setIsLoading(true);
    setLoadingMsg('Conectando con Google...');
    setErrorMessage(null);

    const ready = await ensureGoogleScript();
    if (!ready || !(window as any).google?.accounts?.id) {
      setIsLoading(false);
      setErrorMessage(
        'No se pudo conectar con los servicios de Google. Revisa tu conexión o bloqueador de anuncios.'
      );
      return;
    }

    try {
      (window as any).google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      (window as any).google.accounts.id.prompt();
      setIsLoading(false);
    } catch (err: any) {
      setIsLoading(false);
      setErrorMessage(err.message || 'Error al iniciar Google Sign-In.');
    }
  };

  if (!isOpen) return null;

  const currentEmailDisplay =
    createdUser?.email || regEmail.trim() || authUser?.email || '';
  const currentDisplayName =
    epicUserInfo?.display_name ||
    epicUsername ||
    createdUser?.fortnite_username ||
    '';
  const platformLabel = epicUserInfo?.platform_label || 'Epic Games';
  const isExternalPlatform = Boolean(
    epicUserInfo?.platform && epicUserInfo.platform !== 'epic'
  );
  const platformIconClass =
    epicUserInfo?.platform === 'psn'
      ? 'fa-brands fa-playstation text-blue-500'
      : epicUserInfo?.platform === 'xbl'
      ? 'fa-brands fa-xbox text-emerald-500'
      : epicUserInfo?.platform === 'nintendo'
      ? 'fa-solid fa-gamepad text-red-500'
      : 'fa-solid fa-gamepad text-indigo-500';

  const hasBotsReadyOrWaiting = Boolean(
    botCheckResult &&
      [
        'FRIENDS_READY',
        'FRIENDS_WAITING_48H',
        'BOT_LIMIT_REACHED',
        'NO_BOT_BALANCE',
        'NO_BOT_ITEM_BALANCE',
      ].includes(botCheckResult.status)
  );

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-[rgba(6,23,63,0.78)] backdrop-blur-sm transition-opacity duration-200 select-none ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
      onClick={handleBackdropClick}
    >
      <div
        className={`relative w-full max-w-md bg-white border-2 border-[var(--line)] shadow-2xl p-6 sm:p-7 transition-all duration-200 z-10 ${
          isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        }`}
        style={{
          clipPath:
            'polygon(0 0, calc(100% - 14px) 0, 100% 14px, 100% 100%, 14px 100%, 0 calc(100% - 14px))',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button X (hidden on final success step) */}
        {regStep !== 6 && (
          <button
            type="button"
            onClick={handleClose}
            className="absolute top-4 right-4 sm:top-5 sm:right-5 w-8 h-8 rounded bg-[var(--ice)] hover:bg-[#e2e8f0] text-[var(--soft)] hover:text-[#06173f] flex items-center justify-center transition cursor-pointer z-20 focus:outline-none"
            title="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        {/* ============================================================ */}
        {/* TAB: LOGIN                                                  */}
        {/* ============================================================ */}
        {activeTab === 'login' && (
          <div className="space-y-4">
            <div className="text-center pt-1 pb-1">
              <h3 className="text-xl font-black uppercase text-[#06173f] font-heading tracking-wide">
                Iniciar Sesión
              </h3>
              <p className="text-xs text-[var(--soft)] mt-1 font-sans">
                Ingresa a tu cuenta para continuar
              </p>
            </div>

            {errorMessage && (
              <div
                className="p-3 bg-red-50 border border-red-200 text-xs text-red-600 font-bold flex items-center gap-2"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))',
                }}
              >
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            <form onSubmit={handleLoginSubmit} className="space-y-3.5 pt-1">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#06173f] font-heading mb-1">
                  Correo Electrónico
                </label>
                <div className="relative flex items-center">
                  <div className="absolute left-3.5 flex items-center pointer-events-none text-[var(--soft)]">
                    <Mail className="w-4 h-4" />
                  </div>
                  <input
                    type="email"
                    required
                    value={loginEmail}
                    onChange={(e) => {
                      setLoginEmail(e.target.value);
                      if (errorMessage) setErrorMessage(null);
                    }}
                    placeholder="tuemail@ejemplo.com"
                    className="w-full bg-white border-2 border-[var(--line)] py-2.5 pl-10 pr-4 text-xs font-bold text-[#06173f] placeholder-slate-400 focus:outline-none focus:border-[#1447e6] transition"
                    style={{
                      clipPath:
                        'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))',
                    }}
                  />
                </div>
                {suggestEmailCorrection(loginEmail) && (
                  <div className="mt-1.5 p-2 bg-amber-500/10 border border-amber-500/30 text-amber-900 text-[11px] rounded flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Lightbulb className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                      <span className="truncate">
                        ¿Te refieres a{' '}
                        <strong
                          onClick={() =>
                            setLoginEmail(suggestEmailCorrection(loginEmail)!)
                          }
                          className="font-bold underline cursor-pointer"
                        >
                          {suggestEmailCorrection(loginEmail)}
                        </strong>
                        ?
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setLoginEmail(suggestEmailCorrection(loginEmail)!)
                      }
                      className="px-2 py-0.5 bg-amber-500 text-white font-bold text-[10px] uppercase rounded shadow-sm hover:bg-amber-600"
                    >
                      Corregir
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#06173f] font-heading mb-1">
                  Contraseña
                </label>
                <div className="relative flex items-center">
                  <div className="absolute left-3.5 flex items-center pointer-events-none text-[var(--soft)]">
                    <Lock className="w-4 h-4" />
                  </div>
                  <input
                    type={showLoginPass ? 'text' : 'password'}
                    required
                    value={loginPassword}
                    onChange={(e) => {
                      setLoginPassword(e.target.value);
                      if (errorMessage) setErrorMessage(null);
                    }}
                    placeholder="••••••••"
                    className="w-full bg-white border-2 border-[var(--line)] py-2.5 pl-10 pr-10 text-xs font-bold text-[#06173f] placeholder-slate-400 focus:outline-none focus:border-[#1447e6] transition font-mono"
                    style={{
                      clipPath:
                        'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowLoginPass(!showLoginPass)}
                    className="absolute right-3 text-[var(--soft)] hover:text-[#06173f] p-1 cursor-pointer"
                    title={
                      showLoginPass ? 'Ocultar contraseña' : 'Ver contraseña'
                    }
                  >
                    {showLoginPass ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#1447e6] hover:bg-[#0a2a9c] disabled:opacity-50 text-white font-heading font-black py-3.5 uppercase tracking-wider text-xs transition flex items-center justify-center gap-2 cursor-pointer mt-2 shadow-md"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
                  color: '#ffffff',
                }}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-white" />
                    <span className="text-white">
                      {loadingMsg || 'Ingresando...'}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="text-white">Entrar</span>
                    <ArrowRight className="w-4 h-4 text-white" />
                  </>
                )}
              </button>
            </form>

            {/* Google Divider & Button in Login */}
            <div className="pt-1">
              <div className="relative flex py-2 items-center">
                <div className="flex-grow border-t-2 border-[var(--line)]"></div>
                <span className="flex-shrink mx-2 text-[10px] uppercase font-bold text-[var(--soft)] font-heading tracking-wider">
                  o continúa con
                </span>
                <div className="flex-grow border-t-2 border-[var(--line)]"></div>
              </div>

              <div className="w-full flex justify-center min-h-[44px]">
                <div ref={loginGoogleRef} className="w-full flex justify-center">
                  <button
                    type="button"
                    onClick={handleGoogleAuthClick}
                    className="w-full max-w-[320px] py-2.5 px-4 bg-white hover:bg-[#f8fafc] border-2 border-[var(--line)] font-heading font-bold text-xs text-[#06173f] flex items-center justify-center gap-2.5 transition shadow-sm cursor-pointer"
                    style={{
                      clipPath:
                        'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))',
                    }}
                  >
                    <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                      />
                    </svg>
                    <span>Continuar con Google</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Switch to Register link */}
            <div className="text-center pt-2 border-t-2 border-[var(--line)] mt-3">
              <p className="text-xs text-[var(--soft)]">
                ¿No tienes cuenta?
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab('register');
                    setRegStep(1);
                    setErrorMessage(null);
                  }}
                  className="text-[#1447e6] hover:underline font-bold font-heading transition cursor-pointer ml-1"
                >
                  Crear
                </button>
              </p>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* STEP 1: REGISTRO (CREDENCIALES)                              */}
        {/* ============================================================ */}
        {activeTab === 'register' && regStep === 1 && (
          <div className="space-y-4">
            <div className="text-center pt-1 pb-1">
              <h3 className="text-xl font-black uppercase text-[#06173f] font-heading tracking-wide">
                Crear Cuenta
              </h3>
              <p className="text-xs text-[var(--soft)] mt-1 font-sans">
                Completa tus credenciales para registrarte
              </p>
            </div>

            {pendingPromo && (
              <div
                className="p-3 bg-[var(--ice)] border-2 border-amber-400 text-[#06173f] text-xs font-bold flex items-center gap-3 relative shadow-sm"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))',
                }}
              >
                <div
                  className="w-8 h-8 bg-amber-400 text-slate-950 flex items-center justify-center shrink-0 text-sm font-black shadow"
                  style={{
                    clipPath:
                      'polygon(0 0, calc(100% - 4px) 0, 100% 4px, 100% 100%, 4px 100%, 0 calc(100% - 4px))',
                  }}
                >
                  <Gift className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black uppercase tracking-wider text-amber-700 font-heading">
                      BONO ACTIVO
                    </span>
                    <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 bg-black/10 text-amber-900">
                      {pendingPromo}
                    </span>
                  </div>
                  <div className="text-xs font-bold mt-0.5">
                    ¡Recibes{' '}
                    <span className="text-amber-600 font-black">+300 Pavos</span>{' '}
                    en tu cuenta al registrarte!
                  </div>
                </div>
              </div>
            )}

            {errorMessage && (
              <div
                className="p-3 bg-red-50 border border-red-200 text-xs text-red-600 font-bold flex items-center gap-2"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))',
                }}
              >
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            <form onSubmit={handleRegisterStep1} className="space-y-3 pt-1">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#06173f] font-heading mb-1">
                  Correo Electrónico
                </label>
                <div className="relative flex items-center">
                  <div className="absolute left-3.5 flex items-center pointer-events-none text-[var(--soft)]">
                    <Mail className="w-4 h-4" />
                  </div>
                  <input
                    type="email"
                    required
                    value={regEmail}
                    onChange={(e) => {
                      setRegEmail(e.target.value);
                      if (errorMessage) setErrorMessage(null);
                    }}
                    placeholder="tuemail@ejemplo.com"
                    className="w-full bg-white border-2 border-[var(--line)] py-2.5 pl-10 pr-4 text-xs font-bold text-[#06173f] placeholder-slate-400 focus:outline-none focus:border-[#1447e6] transition"
                    style={{
                      clipPath:
                        'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))',
                    }}
                  />
                </div>
                {suggestEmailCorrection(regEmail) && (
                  <div className="mt-1.5 p-2 bg-amber-500/10 border border-amber-500/30 text-amber-900 text-[11px] rounded flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Lightbulb className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                      <span className="truncate">
                        ¿Te refieres a{' '}
                        <strong
                          onClick={() =>
                            setRegEmail(suggestEmailCorrection(regEmail)!)
                          }
                          className="font-bold underline cursor-pointer"
                        >
                          {suggestEmailCorrection(regEmail)}
                        </strong>
                        ?
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setRegEmail(suggestEmailCorrection(regEmail)!)
                      }
                      className="px-2 py-0.5 bg-amber-500 text-white font-bold text-[10px] uppercase rounded shadow-sm hover:bg-amber-600"
                    >
                      Corregir
                    </button>
                  </div>
                )}
              </div>

              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-[#06173f] font-heading">
                    Contraseña
                  </label>
                  <span className="text-[10px] font-bold text-[var(--soft)] font-heading">
                    Mínimo 8 caracteres
                  </span>
                </div>
                <div className="relative flex items-center">
                  <div className="absolute left-3.5 flex items-center pointer-events-none text-[var(--soft)]">
                    <Lock className="w-4 h-4" />
                  </div>
                  <input
                    type={showRegPass1 ? 'text' : 'password'}
                    required
                    minLength={8}
                    value={regPassword}
                    onChange={(e) => {
                      setRegPassword(e.target.value);
                      if (errorMessage) setErrorMessage(null);
                    }}
                    placeholder="••••••••"
                    className="w-full bg-white border-2 border-[var(--line)] py-2.5 pl-10 pr-10 text-xs font-bold text-[#06173f] placeholder-slate-400 focus:outline-none focus:border-[#1447e6] transition font-mono"
                    style={{
                      clipPath:
                        'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowRegPass1(!showRegPass1)}
                    className="absolute right-3 text-[var(--soft)] hover:text-[#06173f] p-1 cursor-pointer"
                    title={
                      showRegPass1 ? 'Ocultar contraseña' : 'Ver contraseña'
                    }
                  >
                    {showRegPass1 ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#06173f] font-heading mb-1">
                  Confirmar Contraseña
                </label>
                <div className="relative flex items-center">
                  <div className="absolute left-3.5 flex items-center pointer-events-none text-[var(--soft)]">
                    <Lock className="w-4 h-4" />
                  </div>
                  <input
                    type={showRegPass2 ? 'text' : 'password'}
                    required
                    minLength={8}
                    value={regConfirmPassword}
                    onChange={(e) => {
                      setRegConfirmPassword(e.target.value);
                      if (errorMessage) setErrorMessage(null);
                    }}
                    placeholder="••••••••"
                    className="w-full bg-white border-2 border-[var(--line)] py-2.5 pl-10 pr-10 text-xs font-bold text-[#06173f] placeholder-slate-400 focus:outline-none focus:border-[#1447e6] transition font-mono"
                    style={{
                      clipPath:
                        'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowRegPass2(!showRegPass2)}
                    className="absolute right-3 text-[var(--soft)] hover:text-[#06173f] p-1 cursor-pointer"
                    title={
                      showRegPass2 ? 'Ocultar contraseña' : 'Ver contraseña'
                    }
                  >
                    {showRegPass2 ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#1447e6] hover:bg-[#0a2a9c] disabled:opacity-50 text-white font-heading font-black py-3.5 uppercase tracking-wider text-xs transition flex items-center justify-center gap-2 cursor-pointer mt-2 shadow-md"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
                  color: '#ffffff',
                }}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-white" />
                    <span className="text-white">
                      {loadingMsg || 'Procesando...'}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="text-white">Continuar</span>
                    <ArrowRight className="w-4 h-4 text-white" />
                  </>
                )}
              </button>
            </form>

            {/* Google Divider & Button in Registration */}
            <div className="pt-1">
              <div className="relative flex py-2 items-center">
                <div className="flex-grow border-t-2 border-[var(--line)]"></div>
                <span className="flex-shrink mx-2 text-[10px] uppercase font-bold text-[var(--soft)] font-heading tracking-wider">
                  o regístrate con
                </span>
                <div className="flex-grow border-t-2 border-[var(--line)]"></div>
              </div>

              <div className="w-full flex justify-center min-h-[44px]">
                <div
                  ref={registerGoogleRef}
                  className="w-full flex justify-center"
                >
                  <button
                    type="button"
                    onClick={handleGoogleAuthClick}
                    className="w-full max-w-[320px] py-2.5 px-4 bg-white hover:bg-[#f8fafc] border-2 border-[var(--line)] font-heading font-bold text-xs text-[#06173f] flex items-center justify-center gap-2.5 transition shadow-sm cursor-pointer"
                    style={{
                      clipPath:
                        'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))',
                    }}
                  >
                    <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                      />
                    </svg>
                    <span>Continuar con Google</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Switch to Login link */}
            <div className="text-center pt-2 border-t-2 border-[var(--line)] mt-3">
              <p className="text-xs text-[var(--soft)]">
                ¿Ya tienes cuenta?
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab('login');
                    setErrorMessage(null);
                  }}
                  className="text-[#1447e6] hover:underline font-bold font-heading transition cursor-pointer ml-1"
                >
                  Entrar
                </button>
              </p>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* STEP 2: NICK DE FORTNITE (EPIC GAMES)                        */}
        {/* ============================================================ */}
        {activeTab === 'register' && regStep === 2 && (
          <div className="space-y-4">
            <div className="text-center pt-1 pb-1">
              <h3 className="text-xl font-black uppercase text-[#06173f] font-heading tracking-wide">
                Nick de Fortnite
              </h3>
              <p className="text-xs text-[var(--soft)] mt-1 font-sans">
                Ingresa tu Nick exacto de Epic Games
              </p>
            </div>

            {errorMessage && (
              <div
                className="p-3 bg-red-50 border border-red-200 text-xs text-red-600 font-bold flex items-center gap-2"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))',
                }}
              >
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            <form onSubmit={handleRegisterStep2} className="space-y-4 pt-1">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-[#06173f] font-heading uppercase tracking-wider block">
                  Nick de Epic Games
                </label>
                <div className="relative flex items-center">
                  <div className="absolute left-3.5 flex items-center pointer-events-none text-[var(--soft)]">
                    <Gamepad2 className="w-4 h-4" />
                  </div>
                  <input
                    type="text"
                    required
                    value={epicUsername}
                    onChange={(e) => {
                      setEpicUsername(e.target.value);
                      if (errorMessage) setErrorMessage(null);
                    }}
                    placeholder="Ej: Ninja, TuNick..."
                    className="w-full bg-white border-2 border-[var(--line)] py-3 pl-11 pr-4 text-sm font-bold text-[#06173f] focus:outline-none focus:border-[#1447e6] transition font-mono tracking-wide"
                    style={{
                      clipPath:
                        'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))',
                    }}
                    autoFocus
                  />
                </div>
                <p className="text-[10px] text-[var(--soft)] font-sans">
                  Respeta mayúsculas y caracteres especiales.
                </p>
              </div>

              <div className="space-y-2 pt-2">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-[#1447e6] hover:bg-[#0a2a9c] disabled:opacity-50 text-white font-heading font-black py-3.5 uppercase tracking-wider text-xs transition flex items-center justify-center gap-2 cursor-pointer shadow-md"
                  style={{
                    clipPath:
                      'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
                    color: '#ffffff',
                  }}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin text-white" />
                      <span className="text-white">
                        {loadingMsg || 'Buscando jugador...'}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-white">Continuar</span>
                      <ArrowRight className="w-4 h-4 text-white" />
                    </>
                  )}
                </button>

                {!isGoogleUser && (
                  <button
                    type="button"
                    onClick={() => {
                      setErrorMessage(null);
                      setRegStep(1);
                    }}
                    className="w-full text-xs text-[var(--soft)] hover:text-[#1447e6] font-heading font-bold flex items-center justify-center gap-1.5 py-1.5 transition cursor-pointer"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    <span>Volver</span>
                  </button>
                )}
              </div>
            </form>
          </div>
        )}

        {/* ============================================================ */}
        {/* STEP 3: ¿ES TU CUENTA? (CONFIRMACIÓN JUGADOR & BOTS)         */}
        {/* ============================================================ */}
        {activeTab === 'register' && regStep === 3 && (
          <div className="space-y-4">
            <div className="text-center pt-1 pb-1">
              <h3 className="text-xl font-black uppercase text-[#06173f] font-heading tracking-wide">
                ¿Es tu cuenta?
              </h3>
              <p className="text-xs text-[var(--soft)] mt-1 font-sans">
                Confirma tu usuario de Fortnite
              </p>
            </div>

            {/* Found Player Card */}
            <div
              className="bg-[var(--ice)] border-2 border-[var(--line)] p-4 space-y-2"
              style={{
                clipPath:
                  'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
              }}
            >
              <div className="flex items-center justify-between">
                <span
                  className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-300 px-2 py-0.5 flex items-center gap-1 font-heading"
                  style={{
                    clipPath:
                      'polygon(0 0, calc(100% - 4px) 0, 100% 4px, 100% 100%, 4px 100%, 0 calc(100% - 4px))',
                  }}
                >
                  <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                  <span>Jugador Encontrado</span>
                </span>
                <span className="text-[10px] font-mono text-[var(--soft)] font-bold flex items-center gap-1">
                  <i className={isExternalPlatform ? platformIconClass : 'fa-solid fa-shield text-[var(--soft)]'} />
                  <span>{platformLabel}</span>
                </span>
              </div>

              <div className="text-[#06173f] font-heading font-black text-lg sm:text-xl truncate">
                {currentDisplayName}
              </div>

              {isExternalPlatform && (
                <div className="text-[11px] text-[var(--soft)] font-sans flex items-center gap-1.5 pt-0.5">
                  <span className="px-1.5 py-0.5 rounded bg-[var(--line)] text-[10px] font-bold text-[#06173f]">
                    ID de Consola
                  </span>
                  <span>
                    Detectado vía <strong>{platformLabel}</strong> (
                    {epicUserInfo?.external_display_name || epicUsername})
                  </span>
                </div>
              )}

              <p className="text-[11px] text-[var(--soft)] font-sans">
                {hasBotsReadyOrWaiting
                  ? '¡Ya tienes bots de entrega agregados!'
                  : 'Nuestros bots le enviarán solicitud de amistad para habilitar las entregas.'}
              </p>
            </div>

            {/* Privacy blocked warning */}
            {privacyBlockedInfo ? (
              <div
                className="p-3.5 bg-amber-50 border border-amber-200 text-xs text-amber-900 space-y-2"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))',
                }}
              >
                <div className="flex items-center gap-2 font-black text-amber-700 font-heading">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                  <span>Debes desbloquear tu cuenta de Fortnite</span>
                </div>
                <p className="text-[11px] text-amber-800 leading-relaxed font-sans">
                  Ve a Fortnite &gt; Ajustes &gt; Cuenta y Privacidad &gt;
                  Permiso de solicitudes de amistad &gt; selecciona{' '}
                  <strong>Cualquiera</strong> y guarda los cambios.
                </p>
              </div>
            ) : hasBotsReadyOrWaiting ? (
              botCheckResult?.status === 'FRIENDS_READY' ? (
                <div
                  className="bg-emerald-50 border-2 border-emerald-200 p-3.5 space-y-1"
                  style={{
                    clipPath:
                      'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))',
                  }}
                >
                  <div className="flex items-center gap-2 text-emerald-800 font-heading font-black text-xs sm:text-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>Cuentas vinculadas y listas</span>
                  </div>
                  <p className="text-xs text-emerald-700 leading-relaxed font-sans">
                    Tu cuenta <strong className="text-emerald-900">{currentDisplayName}</strong> ya es amiga de nuestros bots de entrega y cumplió las 48 hs de espera.
                  </p>
                </div>
              ) : (
                <div
                  className="bg-amber-50 border-2 border-amber-200 p-3.5 space-y-1"
                  style={{
                    clipPath:
                      'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))',
                  }}
                >
                  <div className="flex items-center gap-2 text-amber-800 font-heading font-black text-xs sm:text-sm">
                    <Clock className="w-4 h-4 text-amber-600 shrink-0" />
                    <span>Período de espera en curso</span>
                  </div>
                  <p className="text-xs text-amber-800 leading-relaxed font-sans">
                    Ya tienes bots de entrega agregados. Faltan aproximadamente{' '}
                    <strong>{botCheckResult?.hours_remaining ?? 48} horas</strong>{' '}
                    para poder recibir regalos.
                  </p>
                </div>
              )
            ) : (
              <div
                className="bg-white border-2 border-[var(--line)] p-3.5 space-y-1"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))',
                }}
              >
                <div className="flex items-center gap-2 text-[#06173f] font-heading font-black text-xs sm:text-sm">
                  <ShieldCheck className="w-4 h-4 text-[#1447e6] shrink-0" />
                  <span>Nuestros bots le enviarán solicitud</span>
                </div>
                <p className="text-xs text-[var(--soft)] leading-relaxed font-sans">
                  Le enviaremos solicitud a{' '}
                  <strong className="text-[#06173f]">{currentDisplayName}</strong>{' '}
                  para iniciar las 48 hs obligatorias de Epic Games.
                </p>
              </div>
            )}

            {errorMessage && (
              <div
                className="p-3 bg-red-50 border border-red-200 text-xs text-red-600 font-bold flex items-center gap-2"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))',
                }}
              >
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  setErrorMessage(null);
                  setPrivacyBlockedInfo(null);
                  setRegStep(2);
                }}
                className="w-1/3 bg-white hover:bg-[var(--ice)] text-[#06173f] border-2 border-[var(--line)] font-heading font-black py-3.5 uppercase tracking-wider text-xs transition cursor-pointer"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))',
                }}
              >
                Atrás
              </button>
              <button
                type="button"
                onClick={handleRegisterStep3Proceed}
                disabled={isLoading}
                className="flex-1 bg-[#1447e6] hover:bg-[#0a2a9c] disabled:opacity-50 text-white font-heading font-black py-3.5 uppercase tracking-wider text-xs transition flex items-center justify-center gap-2 cursor-pointer shadow-md"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
                  color: '#ffffff',
                }}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-white" />
                    <span className="text-white">
                      {loadingMsg || 'Enviando solicitudes...'}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="text-white">
                      {privacyBlockedInfo ? 'Ya lo cambié, reintentar' : 'Continuar'}
                    </span>
                    <ArrowRight className="w-4 h-4 text-white" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* STEP 4: MONEDA (ARS/MXN) Y TELÉFONO OPCIONAL (+54/+52)       */}
        {/* ============================================================ */}
        {activeTab === 'register' && regStep === 4 && (
          <div className="space-y-4">
            <div className="text-center pt-1 pb-1">
              <h3 className="text-xl font-black uppercase text-[#06173f] font-heading tracking-wide">
                Moneda y Contacto
              </h3>
              <p className="text-xs text-[var(--soft)] mt-1 font-sans">
                Elige tu divisa y agrega tu número
              </p>
            </div>

            {errorMessage && (
              <div
                className="p-3 bg-red-50 border border-red-200 text-xs text-red-600 font-bold flex items-center gap-2"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))',
                }}
              >
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            <form onSubmit={handleRegisterStep4Submit} className="space-y-4 pt-1">
              {/* Currency Selector (ARS / MXN) */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-[#06173f] font-heading uppercase tracking-wider block">
                  Selecciona tu Moneda
                </label>
                <div className="grid grid-cols-2 gap-2.5">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCurrency('ARS');
                      setPhonePrefix('+54');
                    }}
                    className={`py-3 px-3 border-2 text-xs font-heading font-black uppercase tracking-wider flex items-center justify-center gap-2 transition cursor-pointer ${
                      selectedCurrency === 'ARS'
                        ? 'border-[#1447e6] bg-[var(--ice)] text-[#1447e6]'
                        : 'border-[var(--line)] bg-white text-[var(--soft)] hover:border-[#1447e6]'
                    }`}
                    style={{
                      clipPath:
                        'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))',
                    }}
                  >
                    <span className="text-base">🇦🇷</span>
                    <span>ARS (Argentina)</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCurrency('MXN');
                      setPhonePrefix('+52');
                    }}
                    className={`py-3 px-3 border-2 text-xs font-heading font-black uppercase tracking-wider flex items-center justify-center gap-2 transition cursor-pointer ${
                      selectedCurrency === 'MXN'
                        ? 'border-[#1447e6] bg-[var(--ice)] text-[#1447e6]'
                        : 'border-[var(--line)] bg-white text-[var(--soft)] hover:border-[#1447e6]'
                    }`}
                    style={{
                      clipPath:
                        'polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px))',
                    }}
                  >
                    <span className="text-base">🇲🇽</span>
                    <span>MXN (México)</span>
                  </button>
                </div>
                <p className="text-[11px] text-[var(--soft)] flex items-center gap-1.5 pt-0.5 font-sans">
                  <Lightbulb className="w-3.5 h-3.5 text-[#1447e6] shrink-0" />
                  <span>Podrás modificar tu moneda luego en tu perfil.</span>
                </p>
              </div>

              {/* Phone Selector (+54 / +52) */}
              <div className="space-y-1.5 pt-1">
                <label className="text-xs font-bold text-[#06173f] font-heading uppercase tracking-wider block">
                  Teléfono / WhatsApp{' '}
                  <span className="text-[var(--soft)] font-normal font-sans lowercase">
                    (opcional)
                  </span>
                </label>
                <div className="flex items-center gap-2">
                  <div className="relative w-32 shrink-0">
                    <select
                      value={phonePrefix}
                      onChange={(e) =>
                        setPhonePrefix(e.target.value as '+54' | '+52')
                      }
                      className="w-full appearance-none bg-white border-2 border-[var(--line)] focus:border-[#1447e6] px-3 py-2.5 text-xs font-bold text-[#06173f] focus:outline-none transition cursor-pointer pr-7"
                      style={{
                        clipPath:
                          'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                      }}
                    >
                      <option value="+54">🇦🇷 +54 (AR)</option>
                      <option value="+52">🇲🇽 +52 (MX)</option>
                    </select>
                    <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-[var(--soft)]">
                      <ChevronDown className="w-3.5 h-3.5" />
                    </div>
                  </div>

                  <input
                    type="text"
                    value={phoneNumber}
                    onChange={(e) => {
                      const cleaned = cleanInputPhoneNumber(
                        e.target.value,
                        phonePrefix
                      );
                      setPhoneNumber(cleaned);
                      if (errorMessage) setErrorMessage(null);
                    }}
                    placeholder="Ej: 9 11 1234-5678"
                    className="flex-1 bg-white border-2 border-[var(--line)] focus:border-[#1447e6] px-4 py-2.5 text-sm font-bold text-[#06173f] focus:outline-none transition font-mono"
                    style={{
                      clipPath:
                        'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                    }}
                  />
                </div>
                <p className="text-[10px] text-[var(--soft)] font-sans">
                  Para avisarte sobre la entrega de tus pedidos.
                </p>
              </div>

              {/* ÚNICO BOTÓN: CONTINUAR */}
              <div className="pt-3">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-[#1447e6] hover:bg-[#0a2a9c] disabled:opacity-50 text-white font-heading font-black py-4 uppercase tracking-wider text-xs sm:text-sm transition flex items-center justify-center gap-2 cursor-pointer shadow-md"
                  style={{
                    clipPath:
                      'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
                    color: '#ffffff',
                  }}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin text-white" />
                      <span className="text-white">
                        {loadingMsg || 'Creando cuenta...'}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-white">Continuar</span>
                      <ArrowRight className="w-4 h-4 text-white" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ============================================================ */}
        {/* STEP 5: VERIFICACIÓN DE CORREO ELECTRÓNICO                   */}
        {/* ============================================================ */}
        {activeTab === 'register' && regStep === 5 && (
          <div className="space-y-4 text-center py-2">
            <div
              className="w-14 h-14 mx-auto rounded bg-amber-100 text-amber-600 flex items-center justify-center text-2xl border-2 border-amber-300 shadow-sm"
              style={{
                clipPath:
                  'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
              }}
            >
              <Mail className="w-7 h-7 text-amber-600" />
            </div>

            <div className="space-y-1">
              <h3 className="text-xl font-black uppercase text-[#06173f] font-heading tracking-wide">
                Verifica tu Correo
              </h3>
              <p className="text-xs text-[var(--soft)] max-w-xs mx-auto leading-relaxed">
                Te enviamos un código de 6 dígitos a{' '}
                <strong className="text-[#06173f] block mt-0.5">
                  {currentEmailDisplay}
                </strong>
              </p>
            </div>

            <form onSubmit={handleVerifyEmailSubmit} className="space-y-3 pt-1">
              <input
                type="text"
                maxLength={6}
                value={verificationCode}
                onChange={(e) => {
                  setVerificationCode(e.target.value.replace(/\D/g, ''));
                  if (verifyError) setVerifyError(null);
                }}
                placeholder="123456"
                className="w-full text-center text-3xl tracking-[10px] font-mono font-black py-3 px-4 border-2 border-[var(--line)] bg-[#f8fafc] text-[#06173f] focus:outline-none focus:border-[#1447e6] uppercase"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
                }}
                autoComplete="one-time-code"
                autoFocus
              />

              {verifyError && (
                <div className="text-xs text-rose-600 font-bold bg-rose-50 border border-rose-200 p-2.5 rounded">
                  {verifyError}
                </div>
              )}

              {verifySuccess && (
                <div className="text-xs text-emerald-600 font-bold bg-emerald-50 border border-emerald-200 p-2.5 rounded">
                  {verifySuccess}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#1447e6] hover:bg-[#0a2a9c] disabled:opacity-50 text-white font-heading font-black py-3.5 px-4 uppercase text-xs tracking-wider transition flex items-center justify-center gap-2 cursor-pointer shadow-md"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
                  color: '#ffffff',
                }}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-white" />
                    <span className="text-white">Verificando...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-white" />
                    <span className="text-white">Confirmar Verificación</span>
                  </>
                )}
              </button>
            </form>

            {/* Skip Button */}
            <div className="pt-1">
              <button
                type="button"
                onClick={() => setRegStep(6)}
                className="w-full bg-white hover:bg-[var(--ice)] border-2 border-[var(--line)] text-[var(--soft)] hover:text-[#06173f] font-heading font-black py-2.5 px-4 uppercase text-xs tracking-wider transition flex items-center justify-center gap-2 cursor-pointer"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
                }}
              >
                <span>Omitir por ahora y continuar</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Resend Code Button */}
            <div className="pt-1 text-center">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={resendCooldown > 0}
                className="text-[11px] font-bold text-[#1447e6] hover:underline inline-flex items-center gap-1.5 font-heading uppercase tracking-wide cursor-pointer disabled:opacity-50"
              >
                {resendCooldown > 0 ? (
                  <>
                    <Clock className="w-3.5 h-3.5" />
                    <span>Reenviar en {resendCooldown}s</span>
                  </>
                ) : (
                  <>
                    <RotateCw className="w-3.5 h-3.5" />
                    <span>Reenviar código</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* STEP 6: PANTALLA FINAL DE ÉXITO                              */}
        {/* ============================================================ */}
        {activeTab === 'register' && regStep === 6 && (
          <div className="flex flex-col items-center justify-center text-center py-4 space-y-4">
            {/* Centered Large Checkmark */}
            <div
              className="w-16 h-16 sm:w-20 sm:h-20 bg-emerald-100 border-2 border-emerald-300 text-emerald-600 flex items-center justify-center text-3xl sm:text-4xl mx-auto shadow-sm"
              style={{
                clipPath:
                  'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))',
              }}
            >
              <Check className="w-9 h-9 sm:w-11 sm:h-11 text-emerald-600 stroke-[3]" />
            </div>

            {/* Success Heading */}
            <div className="space-y-1.5">
              <h3 className="text-xl sm:text-2xl font-black uppercase text-[#06173f] font-heading tracking-wide">
                ¡Cuenta creada con éxito!
              </h3>
              <p className="text-xs sm:text-sm text-[var(--soft)] max-w-sm mx-auto leading-relaxed font-sans">
                Recuerda aceptar las solicitudes de nuestros bots en Fortnite para
                habilitar el envío de tus regalos.
              </p>
            </div>

            {/* Welcome Bonus Card if Applicable */}
            {(promoRewardAwarded > 0 ||
              (createdUser && (createdUser.welcome_bonus_vbucks || 0) > 0)) && (
              <div
                className="w-full p-3 bg-[var(--ice)] border-2 border-amber-400 text-[#06173f] text-xs font-bold flex items-center justify-center gap-3 shadow-sm"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
                }}
              >
                <div
                  className="w-9 h-9 bg-gradient-to-br from-amber-400 to-amber-500 text-slate-950 flex items-center justify-center shrink-0 text-base font-black shadow"
                  style={{
                    clipPath:
                      'polygon(0 0, calc(100% - 4px) 0, 100% 4px, 100% 100%, 4px 100%, 0 calc(100% - 4px))',
                  }}
                >
                  <Gift className="w-5 h-5 text-slate-950" />
                </div>
                <div className="text-left min-w-0">
                  <div className="text-[10px] font-black uppercase text-amber-700 font-heading tracking-wider">
                    ¡BONO DE BIENVENIDA ACREDITADO!
                  </div>
                  <div className="text-xs text-[#06173f] font-bold mt-0.5">
                    Se han acreditado{' '}
                    <span className="text-amber-600 font-black">
                      +
                      {(
                        promoRewardAwarded ||
                        createdUser?.welcome_bonus_vbucks ||
                        300
                      ).toLocaleString('es-AR')}{' '}
                      Pavos
                    </span>{' '}
                    a tu saldo.
                  </div>
                </div>
              </div>
            )}

            {/* Two Action Buttons: Ir a la Tienda & Finalizar */}
            <div className="w-full flex flex-col sm:flex-row gap-3 pt-3">
              <button
                type="button"
                onClick={() => {
                  handleClose();
                  navigateTo('shop', '/tienda');
                }}
                className="flex-1 bg-[#1447e6] hover:bg-[#0a2a9c] text-white font-heading font-black py-3.5 px-4 uppercase text-xs tracking-wider transition flex items-center justify-center gap-2 cursor-pointer shadow-md"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
                  color: '#ffffff',
                }}
              >
                <Store className="w-4 h-4 text-white" />
                <span className="text-white">Ir a la Tienda</span>
              </button>

              <button
                type="button"
                onClick={handleClose}
                className="flex-1 bg-white hover:bg-[var(--ice)] border-2 border-[var(--line)] text-[#06173f] font-heading font-black py-3.5 px-4 uppercase text-xs tracking-wider transition flex items-center justify-center gap-2 cursor-pointer"
                style={{
                  clipPath:
                    'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
                }}
              >
                <span>Finalizar</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
